import { groupBy, sortBy } from "lodash";
import { v4 as uuidv4 } from "uuid"
import moment from "moment";


export const formatAnomalyResults = (anomaliesResults: any) => {
    try {
        const anomalyResultsByEquipmentType = []
        const groupbyEquipmentType = groupBy(anomaliesResults, "equipmentType")
        Object.keys(groupbyEquipmentType).forEach((key) => {
            const critical: any = groupbyEquipmentType[key]?.filter((obj) => obj?.severity === "Critical")?.length || 0
            const major: any = groupbyEquipmentType[key]?.filter((obj) => obj?.severity === "Major")?.length || 0
            const moderate: any = groupbyEquipmentType[key]?.filter((obj) => obj?.severity === "Moderate")?.length || 0
            const total = groupbyEquipmentType[key]?.length
            const criticalPercentage = critical > 0 ? (critical / total) * 100 : 0
            const majorPercentage = major > 0 ? (major / total) * 100 : 0
            const moderatePercentage = moderate > 0 ? (moderate / total) * 100 : 0

            const singleCardDetails = {
                cardTitle: key,
                cardId: uuidv4(), // this is used only for UI purposes
                equipmentType: key,
                critical: critical,
                major: major,
                moderate: moderate,
                total: total,
                criticalPercentage: criticalPercentage !== 0 ? criticalPercentage?.toFixed(0) : 0,
                majorPercentage: majorPercentage !== 0 ? majorPercentage?.toFixed(0) : 0,
                moderatePercentage: moderatePercentage !== 0 ? moderatePercentage?.toFixed(0) : 0,
                tableData: groupbyEquipmentType[key]
            }
            anomalyResultsByEquipmentType.push(singleCardDetails)
        })
        return anomalyResultsByEquipmentType
    } catch (e) {
        return []
    }
}


export const formatAnomalyResultsBuildingsView = (anomaliesResults: any) => {
    try {
        const anomalyResultsByEquipmentType = []
        const groupbyEquipmentType = groupBy(anomaliesResults, "buildingName")
        Object.keys(groupbyEquipmentType).forEach((key) => {
            const critical: any = groupbyEquipmentType[key]?.filter((obj) => obj?.severity === "Critical")?.length || 0
            const major: any = groupbyEquipmentType[key]?.filter((obj) => obj?.severity === "Major")?.length || 0
            const moderate: any = groupbyEquipmentType[key]?.filter((obj) => obj?.severity === "Moderate")?.length || 0
            const total = groupbyEquipmentType[key]?.length
            const criticalPercentage = critical > 0 ? (critical / total) * 100 : 0
            const majorPercentage = major > 0 ? (major / total) * 100 : 0
            const moderatePercentage = moderate > 0 ? (moderate / total) * 100 : 0

            const singleCardDetails = {
                cardTitle: key,
                cardId: uuidv4(), // this is used only for UI purposes
                equipmentType: key,
                critical: critical,
                major: major,
                moderate: moderate,
                total: total,
                criticalPercentage: criticalPercentage !== 0 ? criticalPercentage?.toFixed(0) : 0,
                majorPercentage: majorPercentage !== 0 ? majorPercentage?.toFixed(0) : 0,
                moderatePercentage: moderatePercentage !== 0 ? moderatePercentage?.toFixed(0) : 0,
                tableData: groupbyEquipmentType[key]
            }
            anomalyResultsByEquipmentType.push(singleCardDetails)
        })
        return anomalyResultsByEquipmentType
    } catch (e) {
        return []
    }
}


// converts RDR data to html table string
export const formatRDRDataToTableData = (data, equipmentName) => {
    try {

        const removeProperties = ["time", "bId", "fId", "eId", "dId"]

        const headers = ["Property Date", "Property Time", "Equipment Name"]

        Object.keys(data[0])?.forEach((key) => {
            if (!removeProperties.includes(key)) headers.push(key)
        })

        const headerRow = headers.map(header => `<th>${header}</th>`).join("")

        const rows = data?.map((row) => {
            const cells = headers.map((header) => {
                if (header === "Property Date") {
                    return `<td>${moment(row["time"]).format("DD-MMM-YYYY")}</td>`
                } else if (header === "Property Time") {
                    return `<td>${moment(row["time"]).format("hh:mm A")}</td>`
                } else if (header === "Equipment Name") {
                    return `<td>${equipmentName}</td>`
                } else {
                    return `<td>${row[header]}</td>`
                }
            }).join("")
            return `<tr>${cells}</tr>`
        })
        return "```html " + `<table><thead><tr>${headerRow}</tr></thead><tbody>${rows?.join("")}</tbody></table>` + "```"
    } catch (e) {
        return ""
    }

}


export const getRandomColor = () => {
    const letters = "0123456789ABCDEF"
    let color = "#"
    for (let i = 0; i < 6; i++) {
        color += letters[Math.floor(Math.random() * 16)]
    }
    return color
}


export const formatToChartData = (response, equipmentType, equipmentId, equipmentMetaData, uomUnits) => {
    try {
        const removeProperties = ["time", "bId", "fId", "eId", "dId"]
        const timestamps = response?.map(x => {
            const splitedTime = x?.time?.split(" ")
            return `${splitedTime?.[0]}T${splitedTime?.[1]}`
        })
        let uniqueDOMs = []

        // key and display name store
        const propertyKeys = []

        const getPropData = () => {
            const data = {}
            Object.keys(response[0])?.forEach((responseDisplayName) => {
                if (!removeProperties.includes(responseDisplayName)) {
                    const propMetaData = equipmentMetaData?.find((x) => x?.tisDisplayName === responseDisplayName)
                    if (propMetaData) {
                        const unitDetails = uomUnits?.find(x => x?.UoM === propMetaData?.targetMeasureUnit && x?.subType === propMetaData?.targetMeasureType)
                        const keyName = `${equipmentId}_&&_${propMetaData?.targetKey}`
                        propertyKeys.push({
                            targetKey: propMetaData?.targetKey || "",
                            displayName: propMetaData?.tisDisplayName || "",
                            symbol: unitDetails?.Symbol || ""
                        })
                        const values = response?.map(x => x?.[responseDisplayName] ? x?.[responseDisplayName] : null)
                        data[keyName] = {
                            data: values
                        }
                    }
                }
            })
            return data
        }

        const getLegends = () => {
            uniqueDOMs = [...new Set(propertyKeys?.map(x => x?.symbol))]?.filter(x => x).slice(0, 4)

            const legends = {}

            propertyKeys?.forEach((property) => {
                if (uniqueDOMs?.includes(property?.symbol)) {
                    const keyName = `${equipmentId}_&&_${property?.targetKey}`
                    const axisIndex = uniqueDOMs?.findIndex(x => x === property?.symbol) + 1
                    legends[keyName] = {
                        id: keyName,
                        name: property?.displayName,
                        axisName: `y${axisIndex}`,
                        symbol: property?.symbol,
                        show: true,
                        visible: true,
                        type: "line",
                        lineType: "solid",
                        strokeWidth: "1",
                        shapeSize: "1",
                        isAdded: false,
                        equipmentType: equipmentType,
                        color: getRandomColor(),
                        shape: "dot"
                    }
                }
            })
            return legends
        }


        const getAxisList = () => {
            const axisList = []
            uniqueDOMs?.forEach((dom, index) => {
                axisList.push({
                    "symbol": dom,
                    "axisName": `y${index + 1}`,
                    "axisType": "y",
                    // "range": [
                    //     -2.5,
                    //     11.1
                    // ]
                })
            })

            return axisList
        }

        if (timestamps?.length > 0) {
            const startDate = timestamps?.[0]
            const endDate = timestamps?.[timestamps?.length - 1]
            const data = getPropData()
            const legends = getLegends()
            const axisList = getAxisList()
            return {
                start: startDate,
                end: endDate,
                data,
                chartProps: {
                    axisList,
                    legends,
                    timestamps
                }
            }
        }


    } catch (e) {
        return null
    }
}


export const getSpecialSubStringByChar = (specialCharStart = "[", specialCharEnd = "]", data = "") => {
    let dataArray = ""
    const startIndex = data.indexOf(specialCharStart);
    let endIndex = startIndex;
    let i = startIndex
    if (startIndex !== -1) {
        let bracketCount = 0;
        // Iterate through the string only once
        for (i; i <= data.length; i++) {
            if (data[i] === specialCharStart) {
                bracketCount = bracketCount + 1;
            } else if (data[i] === specialCharEnd) {
                bracketCount = bracketCount - 1;
                if (bracketCount === 0) {
                    endIndex = i + 1;
                    break;
                }
            }
        }
        // Extract the array string
        dataArray = data.substring(startIndex, endIndex);
        return dataArray
    }
}


export const getCharDataRequestFormat = ({ start_date, end_date, equipment_type, building_id, equipment_id, equipmentName, propertyKeys }) => {
    // Currently it supports for single building and single equipment combinations
    // based on future needs, we may change this format
    const equipmentDetails = [
        {
            type: equipment_type,
            equipmentName: equipmentName,
            eId: [
                {
                    id: equipment_id
                }
            ],
            pId: propertyKeys
        }
    ]
    return {
        startDate: start_date,
        endDate: end_date,
        category: "Equipment",
        chartType: "Line",
        building: {
            id: building_id,
            pId: [],
            type: "Location"
        },
        equipment: equipmentDetails,
        equipmentType: equipment_type,
        weather: [],
        settings: [],
        buildingDateTime: "2024-01-08 23:26:43"
    }
}